import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { resolveTimeZone } from '@/data/utils/eddDateCalculation';

const TIME_PATTERN = /^(\d{1,2}):(\d{2})\s*(a\.?\s?m\.?|p\.?\s?m\.?)?/;

const normalizeDayName = (value: string): string =>
	value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/**
 * Parses the store-locator JSON time format ("10:00 p.m.", "08:00 a.m.") — and a
 * plain 24h "22:00" — into minutes since midnight. Returns `undefined` when the
 * value can't be read, so callers never act on a guessed close time.
 */
export const parseStoreTimeToMinutes = (time?: string | null): number | undefined => {
	const match = TIME_PATTERN.exec(
		String(time ?? '')
			.trim()
			.toLowerCase()
	);
	if (!match) return undefined;

	let hours = Number.parseInt(match[1], 10);
	const minutes = Number.parseInt(match[2], 10);
	const period = match[3]?.replace(/[.\s]/g, '');

	if (period === 'pm' && hours !== 12) hours += 12;
	if (period === 'am' && hours === 12) hours = 0;

	return hours * 60 + minutes;
};

/**
 * Closing time, in minutes since midnight, of the given physical store for the
 * weekday of `now` — read from `storeLocatorStateCitiesList.json` (DIY schedule,
 * Prostore as fallback). The weekday is resolved in the same timezone
 * `getHoursAndMinutes` uses, so day and hour always refer to the same instant.
 * Returns `undefined` when the store, the day, or a parsable close time is missing.
 */
export const getStoreCloseMinutes = (
	physicalStoreId: string | undefined,
	now: Date = new Date()
): number | undefined => {
	if (!physicalStoreId) return undefined;

	const store = getStoreDataByPhysicalStoreId(physicalStoreId) as
		| (ReturnType<typeof getStoreDataByPhysicalStoreId> & {
				scheduleProstore?: { schedule?: { dayName: string; close: string }[] };
		  })
		| undefined;
	const schedule = store?.scheduleDiystore?.schedule ?? store?.scheduleProstore?.schedule;
	if (!schedule?.length) return undefined;

	const today = normalizeDayName(
		new Intl.DateTimeFormat('es-MX', { weekday: 'long', timeZone: resolveTimeZone() }).format(now)
	);
	const todaySchedule = schedule.find((day) => normalizeDayName(day.dayName) === today);

	return parseStoreTimeToMinutes(todaySchedule?.close);
};
